/**
 * 사용자 행동 추적 SDK (Phase 1 — 온톨로지 V0).
 *
 * 핵심 측정값:
 *   - dwellMs   = 페이지 진입 → 떠난 총 시간
 *   - activeMs  = 마우스/스크롤/키보드/포커스 활동이 있는 시간만 (잠수 시간 제외)
 *
 * 활성도 판정: activity event 발생 시 lastActivity 갱신.
 *   — 30초 이상 무활동 = 잠수 → activeMs 카운트 중단.
 *
 * 전송 전략 (Phase 1 강화):
 *   1) 큐 (in-memory) 에 누적 → 5건 또는 3초 마다 flush (fetch + keepalive)
 *   2) visibilitychange/pagehide → 즉시 sendBeacon 으로 잔여분 flush
 *   3) fetch 실패 시 1회 재시도 (exponential backoff 50ms · 단순)
 *
 * 메타 자동 부착:
 *   - 세션 메타 (utm/landingReferrer/deviceType): 첫 landing 1회 캡처 → sessionStorage
 *   - 요청 메타 (viewport/locale/timezone/prefersColorScheme): 매 이벤트
 *
 * PII 보호:
 *   - path / label / meta 모두 redact 처리 (이메일·전화·카드번호·주민번호 마스킹)
 *   - sensitive query key 는 *** 로
 *
 * 동의 게이트 (Phase 0):
 *   - hasAnalyticsConsent() === false 면 enqueue 자체를 skip
 */

import { hasAnalyticsConsent } from "@/lib/consent";
import { isBiteMeasurementPath, trackBiteEvent } from './bite-measurement';
import {
  captureSessionMetaIfFirstVisit,
  getSessionMeta,
  captureRequestMeta,
  type SessionMeta,
  type RequestMeta,
} from "@/lib/analytics-meta";
import { redactPath, redactLabel, redactMeta } from "@/lib/analytics-redact";
import {
  ALLOWED_EVENT_TYPES,
  isAllowedClickLabel,
  isAllowedCustomLabel,
} from "@/lib/analytics-whitelist";

// ─────────────────────────────────────────────────────────────
// 세션 식별자
// ─────────────────────────────────────────────────────────────
const SESSION_KEY = "bs:analytics:session-id";
const IDLE_THRESHOLD_MS = 30_000;

// 큐 / 배치 파라미터
const FLUSH_INTERVAL_MS = 3_000;
const FLUSH_THRESHOLD = 5;
const MAX_QUEUE_SIZE = 100; // 폭주 방지 — 초과 시 가장 오래된 항목 drop
const RETRY_DELAY_MS = 50;

function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "_anon";
  }
}

// ─────────────────────────────────────────────────────────────
// 이벤트 타입
// ─────────────────────────────────────────────────────────────

export type EventType = "pageview" | "dwell" | "click" | "custom";

interface BaseEvent {
  type: EventType;
  path?: string;
  label?: string;
  dwellMs?: number;
  activeMs?: number;
  referrer?: string;
  meta?: Record<string, unknown>;
}

interface EnrichedEvent extends BaseEvent {
  sessionId: string;
  ts: number;
  session: SessionMeta;
  request: RequestMeta;
}

// ─────────────────────────────────────────────────────────────
// 큐 + 배치
// ─────────────────────────────────────────────────────────────

const queue: EnrichedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush(false);
  }, FLUSH_INTERVAL_MS);
}

/**
 * 큐 비우기.
 * @param viaUnload true 면 sendBeacon 사용 (페이지 unload 보장), false 면 fetch
 * @returns 성공 여부
 */
async function flush(viaUnload: boolean): Promise<boolean> {
  if (queue.length === 0) return true;
  const batch = queue.splice(0, queue.length);
  const payload = JSON.stringify({ events: batch });

  if (viaUnload && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      const blob = new Blob([payload], { type: "application/json" });
      const ok = navigator.sendBeacon("/api/analytics/event", blob);
      if (!ok) {
        // beacon 실패 — fetch 로 fallback (keepalive)
        return sendViaFetch(payload, true);
      }
      return true;
    } catch {
      return sendViaFetch(payload, true);
    }
  }

  return sendViaFetch(payload, false);
}

async function sendViaFetch(payload: string, keepalive: boolean): Promise<boolean> {
  try {
    const res = await fetch("/api/analytics/event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: payload,
      keepalive,
    });
    if (!res.ok) {
      // 1회 재시도
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      const retry = await fetch("/api/analytics/event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: payload,
        keepalive,
      });
      return retry.ok;
    }
    return true;
  } catch {
    return false;
  }
}

function enqueue(evt: BaseEvent) {
  if (typeof window === "undefined") return;
  // 1) 동의 게이트
  if (!hasAnalyticsConsent()) return;
  // 2) 화이트리스트 — type
  if (!ALLOWED_EVENT_TYPES.has(evt.type)) return;
  // 3) 화이트리스트 — click label
  if (evt.type === "click" && evt.label && !isAllowedClickLabel(evt.label)) return;
  // 4) 화이트리스트 — custom label
  if (evt.type === "custom" && evt.label && !isAllowedCustomLabel(evt.label)) return;

  // 5) 메타 부착 + redaction
  const sessionId = getOrCreateSessionId();
  const session = getSessionMeta();
  const request = captureRequestMeta();

  const path = evt.path || window.location.pathname || "/";
  const enriched: EnrichedEvent = {
    type: evt.type,
    path: redactPath(path),
    label: evt.label ? redactLabel(evt.label) : undefined,
    dwellMs: evt.dwellMs,
    activeMs: evt.activeMs,
    referrer: evt.referrer ? redactPath(evt.referrer) : undefined,
    meta: evt.meta ? (redactMeta(evt.meta) as Record<string, unknown>) : undefined,
    sessionId,
    ts: Date.now(),
    session,
    request,
  };

  queue.push(enriched);

  // 폭주 방지 — 오래된 것부터 drop
  while (queue.length > MAX_QUEUE_SIZE) queue.shift();

  // flush 트리거
  if (queue.length >= FLUSH_THRESHOLD) {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    void flush(false);
  } else {
    scheduleFlush();
  }
}

/**
 * 페이지 떠날 때 즉시 flush (sendBeacon 으로 unload 보장).
 * AnalyticsTracker 가 visibilitychange / pagehide 에서 호출.
 */
export function flushNow() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  void flush(true);
}

// ─────────────────────────────────────────────────────────────
// 공개 API (기존 호출자 호환)
// ─────────────────────────────────────────────────────────────

export function trackPageview(path?: string) {
  // 첫 호출 시 세션 메타 캡처 (idempotent)
  captureSessionMetaIfFirstVisit();
  enqueue({
    type: "pageview",
    path: path || (typeof window !== "undefined" ? window.location.pathname : ""),
    referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
  });
}

export function trackClick(label: string, meta?: Record<string, unknown>) {
  if (typeof window !== 'undefined' && isBiteMeasurementPath(location.pathname)) {
    trackBiteEvent('click', { ...meta, label });
    return;
  }
  enqueue({ type: "click", label, meta });
}

export function trackCustom(label: string, meta?: Record<string, unknown>) {
  if (typeof window !== 'undefined' && isBiteMeasurementPath(location.pathname)) {
    trackBiteEvent('click', { ...meta, label });
    return;
  }
  enqueue({ type: "custom", label, meta });
}

// ─────────────────────────────────────────────────────────────
// dwell tracker
// ─────────────────────────────────────────────────────────────

/**
 * 페이지별 dwell tracker — usePageDwellTracker hook 의 내부 사용.
 *
 * 시작: 함수 호출 즉시
 * 종료: flush() — 페이지 unmount or visibilitychange (hidden)
 */
export function createDwellTracker(path: string) {
  let startedAt = Date.now();
  let lastActivity = Date.now();
  let activeAccumMs = 0;
  let lastTickAt = Date.now();
  let stopped = false;
  let activityRaf: number | null = null;

  const onActivity = () => {
    lastActivity = Date.now();
  };
  const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "focus"];
  events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

  // 1초마다 tick — active time 누적
  const tick = () => {
    if (stopped) return;
    const now = Date.now();
    const elapsed = now - lastTickAt;
    lastTickAt = now;
    if (now - lastActivity < IDLE_THRESHOLD_MS) {
      activeAccumMs += elapsed;
    }
    activityRaf = window.setTimeout(tick, 1000) as unknown as number;
  };
  activityRaf = window.setTimeout(tick, 1000) as unknown as number;

  const flushDwell = () => {
    if (stopped) return;
    stopped = true;
    if (activityRaf) clearTimeout(activityRaf);
    events.forEach((e) => window.removeEventListener(e, onActivity));
    const dwellMs = Date.now() - startedAt;
    enqueue({ type: "dwell", path, dwellMs, activeMs: activeAccumMs });
  };

  return { flush: flushDwell };
}

// ─────────────────────────────────────────────────────────────
// React hook helper (placeholder — analytics-tracker.tsx 가 직접 createDwellTracker 사용)
// ─────────────────────────────────────────────────────────────

export function usePageTracker(_path?: string) {
  if (typeof window === "undefined") return;
  // 본 함수는 helper 형태로만 노출 — 실제 사용은 analytics-tracker.tsx 에서.
  void _path;
}
